<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">券码核销</h2>
      </div>
      <div class="page-header-extra">
        <span>今日核销 <b>{{ stats.success }}</b> 张</span>
        <span class="divider">|</span>
        <span class="text-danger">今日失败 <b>{{ stats.fail }}</b> 次</span>
        <span class="divider">|</span>
        <span>核销员 <b>{{ operatorName }}</b></span>
      </div>
    </div>

    <div class="checkin-grid">
      <!-- 左：核销操作 -->
      <el-card class="checkin-card" shadow="never">
        <div class="checkin-label">扫码 / 输入核销码</div>
        <el-input
          ref="codeInput"
          v-model="code"
          class="code-input"
          placeholder="扫码枪对准用户出示的二维码，或手动输入核销码"
          clearable
          @keyup.enter.native="handleVerify"
        >
          <i slot="prefix" class="el-icon-camera-solid" />
        </el-input>
        <div class="checkin-tip">
          <i class="el-icon-info" />
          扫码枪输入后会自动回车直接核销；手动输入请核对无误后回车或点「确认核销」。
        </div>

        <div class="checkin-actions">
          <el-button
            type="primary"
            size="medium"
            icon="el-icon-s-check"
            :loading="verifying"
            @click="handleVerify"
          >确认核销</el-button>
          <el-button
            type="danger"
            plain
            size="medium"
            icon="el-icon-delete"
            :loading="voiding"
            @click="handleVoid"
          >作 废</el-button>
          <el-button size="medium" @click="resetAll">清 空</el-button>
        </div>
      </el-card>

      <!-- 右：核销结果 -->
      <el-card class="result-card" shadow="never">
        <div v-if="!result" class="result-empty">
          <i class="el-icon-camera result-empty-icon" />
          <div class="result-empty-title">等待核销</div>
          <div class="result-empty-desc">扫描核销码后，这里会显示券信息与核销结果</div>
        </div>

        <div v-else class="result-body">
          <div class="result-head" :class="result.result === 1 ? 'is-ok' : 'is-fail'">
            <i :class="result.result === 1 ? 'el-icon-check' : 'el-icon-close'" />
            <span>{{ result.result === 1 ? '核销成功' : '核销失败' }}</span>
          </div>

          <div v-if="result.target" class="result-ticket">
            <el-image :src="result.target.benefit_cover" :preview-src-list="[result.target.benefit_cover]" fit="cover" class="result-cover" />
            <div class="result-main">
              <div class="result-name">{{ result.target.benefit_name || '—' }}</div>
              <div class="result-holder">
                {{ result.target.nickname || '未设置昵称' }}（编号 {{ result.target.user_id }}）
              </div>
            </div>
          </div>
          <div v-else class="result-ticket result-ticket--unknown">
            <div class="result-name">未查询到该核销码</div>
            <div class="result-holder">请确认扫码来源，或让用户在小程序刷新后重新出示</div>
          </div>

          <div class="result-fields">
            <div class="rf">
              <span>核销码</span>
              <b class="rf-code">{{ formatVerifyCode(result.record.verify_code) }}</b>
            </div>
            <div v-if="result.result === 1" class="rf">
              <span>券有效期</span>
              <!-- expire_time 为空 = 长期有效券种，只显示生效时间，不拼「至 」 -->
              <b>{{ day(result.target.start_time) }}{{ result.target.expire_time ? ' 至 ' + day(result.target.expire_time) : ' 起长期有效' }}</b>
            </div>
            <div v-else class="rf">
              <span>失败原因</span>
              <b class="text-danger">{{ result.fail_reason }}</b>
            </div>
            <div class="rf">
              <span>核销时间</span>
              <b>{{ result.record.verify_time }}</b>
            </div>
          </div>

          <div class="result-hint" :class="result.result === 1 ? 'is-ok' : 'is-fail'">{{ resultHint }}</div>
        </div>
      </el-card>
    </div>

    <!-- 本次核销流水 -->
    <el-card class="session-card" shadow="never">
      <div class="session-head">
        <span class="session-title">本次核销流水</span>
        <el-button type="text" size="mini" @click="goRecords">
          查看全部核销记录 <i class="el-icon-arrow-right" />
        </el-button>
      </div>
      <el-table v-if="sessionLogs.length" :data="sessionLogs" border size="small" max-height="250">
        <el-table-column label="核销码" width="150">
          <template slot-scope="scope">
            <span class="rf-code">{{ formatVerifyCode(scope.row.verify_code) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="持券人" min-width="140" prop="nickname" show-overflow-tooltip />
        <el-table-column label="券名称" min-width="170" prop="benefit_name" show-overflow-tooltip />
        <el-table-column label="结果" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="verifyResultTagType(scope.row.result)" size="mini" effect="plain">
              {{ verifyResultText(scope.row.result) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="失败原因" width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.fail_reason" class="text-danger">{{ scope.row.fail_reason }}</span>
            <span v-else class="cell-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="核销时间" width="160" prop="verify_time" show-overflow-tooltip />
      </el-table>
      <div v-else class="session-empty">
        本次登录还没有核销操作，扫码核销后会在这里连续显示
      </div>
    </el-card>
  </div>
</template>

<script>
import { formatVerifyCode } from '@/mock/userBenefit'
import { verifyResultText, verifyResultTagType } from '@/mock/benefitVerify'
import { checkinVerify, checkinVoid, getVerifyTodayStats } from '@/api/benefit'

export default {
  name: 'BenefitCheckin',
  data() {
    return {
      code: '',
      verifying: false,
      voiding: false,
      result: null,
      sessionLogs: [],
      stats: { success: 0, fail: 0 },
      formatVerifyCode,
      verifyResultText,
      verifyResultTagType
    }
  },
  computed: {
    operatorName() {
      return this.$store.getters.getUserName || '当前管理员'
    },
    resultHint() {
      if (!this.result) return ''
      if (this.result.result === 1) {
        return '已核销完成，请引导用户进入，祝旅途愉快。'
      }
      const reason = this.result.fail_reason
      if (reason === '券码已核销') {
        const t = this.result.target && this.result.target.use_time
        return t ? `该券已于 ${t} 核销过，请提醒用户不要重复出示。` : '该券已核销过，请提醒用户不要重复出示。'
      }
      if (reason === '券码已过期') {
        return '该券已超出有效期，如需继续使用请联系运营补发。'
      }
      return '未查询到该核销码，请确认是否输错或来自其他平台。'
    }
  },
  created() {
    this.refreshStats()
    this.focusInput()
  },
  methods: {
    day(text) {
      return (text || '').slice(0, 10)
    },
    // 今日核销统计（后端统计）
    refreshStats() {
      getVerifyTodayStats().then(res => {
        if (res.code !== 200) return
        this.stats = res.data || { success: 0, fail: 0 }
      })
    },
    focusInput() {
      this.$nextTick(() => {
        const input = this.$refs.codeInput
        if (input && input.focus) input.focus()
      })
    },
    // 核销：扫码枪回车即触发；后端按券状态判定，失败同样返 200，前端按失败原因提示
    handleVerify() {
      const code = (this.code || '').trim()
      if (!code) {
        this.$message.warning('请先扫描或输入核销码')
        this.focusInput()
        return
      }
      if (this.verifying) return
      this.verifying = true
      checkinVerify({ code }).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.result = data
        if (data.record) this.sessionLogs.unshift(data.record)
        this.refreshStats()
        this.code = ''
        if (data.result === 1) {
          this.$message.success('核销成功')
        } else {
          this.$message.error('核销失败：' + (data.fail_reason || '未知原因'))
        }
      }).finally(() => {
        this.verifying = false
        this.focusInput()
      })
    },
    // 作废：券置为已作废，用户无法再使用；已核销/已过期/已作废的不可作废
    handleVoid() {
      const code = (this.code || '').trim()
      if (!code) {
        this.$message.warning('请先扫描或输入要作废的核销码')
        this.focusInput()
        return
      }
      this.$confirm('确认作废该券？作废后用户无法使用，且不可恢复。', '作废确认', {
        type: 'warning',
        confirmButtonText: '确认作废',
        cancelButtonText: '取 消'
      }).then(() => {
        if (this.voiding) return
        this.voiding = true
        checkinVoid({ code }).then(res => {
          if (res.code !== 200) return
          const data = res.data || {}
          this.code = ''
          this.result = null
          if (data.result === 1) {
            const t = data.target || {}
            this.$message.success(`已作废：${t.benefit_name || '—'}（${t.nickname || '未知用户'}）`)
          } else {
            this.$message.warning(data.message || '作废失败')
          }
        }).finally(() => {
          this.voiding = false
          this.focusInput()
        })
      }).catch(() => {})
    },
    resetAll() {
      this.code = ''
      this.result = null
      this.focusInput()
    },
    goRecords() {
      this.$router.push('/benefit/verify')
    }
  }
}
</script>

<style scoped>
.checkin-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 14px;
  margin-bottom: 14px;
}
.checkin-card >>> .el-card__body {
  padding: 20px 22px 22px;
}
.checkin-label {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  padding-left: 9px;
  border-left: 3px solid var(--primary);
  margin-bottom: 12px;
}
/* 核销码大输入框 */
.code-input >>> .el-input__inner {
  height: 58px;
  line-height: 58px;
  font-size: 19px;
  letter-spacing: 3px;
  font-family: Consolas, Monaco, monospace;
  padding-left: 44px;
}
.code-input >>> .el-input__prefix {
  left: 14px;
  font-size: 20px;
  color: var(--primary);
  line-height: 58px;
}
.checkin-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.checkin-tip i {
  color: #b8ac9c;
  margin-right: 4px;
}
.checkin-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 22px;
}
.checkin-actions >>> .el-button--primary {
  min-width: 150px;
}
/* 结果卡 */
.result-card >>> .el-card__body {
  padding: 20px 22px;
  min-height: 292px;
}
.result-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 252px;
  text-align: center;
}
.result-empty-icon {
  font-size: 46px;
  color: #ded4c6;
}
.result-empty-title {
  margin-top: 14px;
  font-size: 15px;
  font-weight: 600;
  color: #8c8578;
}
.result-empty-desc {
  margin-top: 8px;
  font-size: 12px;
  color: #b3ab9e;
  line-height: 1.6;
  max-width: 230px;
}
.result-head {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 62px;
  border-radius: 8px;
  font-size: 19px;
  font-weight: 600;
  color: #fff;
}
.result-head i {
  font-size: 24px;
}
.result-head.is-ok {
  background: linear-gradient(120deg, #2f6b5e, #4f9c85);
}
.result-head.is-fail {
  background: linear-gradient(120deg, #a53a2c, #c9594a);
}
.result-ticket {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  padding: 12px;
  border-radius: 8px;
  background: #faf7f1;
  border: 1px solid #f0ebe2;
}
.result-ticket--unknown {
  display: block;
}
.result-cover {
  flex: 0 0 72px;
  width: 72px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  background: #ece3d6;
}
.result-main {
  min-width: 0;
}
.result-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}
.result-holder {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.result-fields {
  margin-top: 12px;
}
.rf {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px dashed #f0f0f0;
  font-size: 12.5px;
}
.rf:last-child {
  border-bottom: none;
}
.rf > span {
  flex: 0 0 66px;
  color: #909399;
}
.rf > b {
  flex: 1;
  text-align: right;
  font-weight: 500;
  color: #303133;
  word-break: break-all;
}
.rf-code {
  font-family: Consolas, Monaco, monospace;
  letter-spacing: 0.6px;
}
.result-hint {
  margin-top: 12px;
  padding: 9px 11px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
}
.result-hint.is-ok {
  background: #f0f7f4;
  color: #2f6b5e;
}
.result-hint.is-fail {
  background: #fdf1ef;
  color: #a53a2c;
}
/* 本次流水 */
.session-card >>> .el-card__body {
  padding: 16px 20px;
}
.session-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.session-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.session-empty {
  padding: 26px 0;
  text-align: center;
  font-size: 12.5px;
  color: #b3ab9e;
}
.cell-muted {
  color: #c0c4cc;
}
.text-danger {
  color: #d9534f;
}
@media (max-width: 1180px) {
  .checkin-grid {
    grid-template-columns: 1fr;
  }
}
</style>
